import React from 'react';
import { Modal, Tag } from 'antd';
import {
  UserOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  LaptopOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  InfoCircleOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import moment from 'moment';

const AuditDetailModal = ({ visible, onClose, record }) => {
  if (!record) return null;

  const formatJson = (str) => {
    if (!str) return '—';
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch {
      return str;
    }
  };

  const statusColorMap = {
    SUCCESS: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
    FAILED: { color: '#ff4d4f', bg: '#fff2f0', border: '#ffa39e' },
    PENDING: { color: '#faad14', bg: '#fffbe6', border: '#ffe58f' },
  };

  const sc = statusColorMap[record.status] || statusColorMap.PENDING;
  const hasDiff = record.oldValues || record.newValues;

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
          <ThunderboltOutlined style={{ color: '#ff6700' }} />
          Audit Event Detail — #{record.id}
        </span>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={720}
      className="audit-detail-modal"
      centered
    >
      {/* ── User & Identity Section ──────────────── */}
      <div className="audit-detail-section-title">
        <UserOutlined /> Identity Information
      </div>
      <div className="audit-detail-grid">
        <div className="audit-detail-item">
          <span className="audit-detail-label">
            <UserOutlined /> User ID
          </span>
          <span className="audit-detail-value" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {record.userId || '—'}
          </span>
        </div>
        <div className="audit-detail-item">
          <span className="audit-detail-label">
            <KeyOutlined /> Session ID
          </span>
          <span className="audit-detail-value" style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {record.sessionId || '—'}
          </span>
        </div>
        <div className="audit-detail-item">
          <span className="audit-detail-label">
            <GlobalOutlined /> IP Address
          </span>
          <span className="audit-detail-value" style={{ fontFamily: 'monospace' }}>
            {record.ipAddress || '—'}
          </span>
        </div>
        <div className="audit-detail-item">
          <span className="audit-detail-label">
            <LaptopOutlined /> User Agent
          </span>
          <span className="audit-detail-value" style={{ fontSize: 12, wordBreak: 'break-all' }}>
            {record.userAgent || '—'}
          </span>
        </div>
      </div>

      <hr className="audit-detail-divider" style={{ margin: '20px 0' }} />

      {/* ── Event Metadata Section ──────────────── */}
      <div className="audit-detail-section-title">
        <InfoCircleOutlined /> Event Details
      </div>
      <div className="audit-detail-grid">
        <div className="audit-detail-item">
          <span className="audit-detail-label">Event Type</span>
          <span className="audit-detail-value">
            <Tag
              style={{
                background: '#fff3eb',
                color: '#ff6700',
                border: '1px solid #ffd8b8',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 11,
              }}
            >
              {record.eventType || '—'}
            </Tag>
          </span>
        </div>
        <div className="audit-detail-item">
          <span className="audit-detail-label">
            <AppstoreOutlined /> Entity Type
          </span>
          <span className="audit-detail-value">
            <Tag
              style={{
                background: '#f5f5f5',
                color: '#595959',
                border: '1px solid #e8e8e8',
                borderRadius: 6,
                fontWeight: 500,
              }}
            >
              {record.entityType || '—'}
            </Tag>
          </span>
        </div>
        <div className="audit-detail-item">
          <span className="audit-detail-label">Entity ID</span>
          <span className="audit-detail-value" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {record.entityId || '—'}
          </span>
        </div>
        <div className="audit-detail-item">
          <span className="audit-detail-label">
            <ThunderboltOutlined /> Action
          </span>
          <span className="audit-detail-value" style={{ fontWeight: 600 }}>
            {record.action || '—'}
          </span>
        </div>
        <div className="audit-detail-item full-width">
          <span className="audit-detail-label">Description</span>
          <span className="audit-detail-value">
            {record.description || '—'}
          </span>
        </div>
        <div className="audit-detail-item">
          <span className="audit-detail-label">Status</span>
          <span className="audit-detail-value">
            <Tag
              style={{
                color: sc.color,
                background: sc.bg,
                border: `1px solid ${sc.border}`,
                borderRadius: 20,
                fontWeight: 700,
                fontSize: 11,
                padding: '2px 12px',
              }}
            >
              {record.status || '—'}
            </Tag>
          </span>
        </div>
        <div className="audit-detail-item">
          <span className="audit-detail-label">
            <ClockCircleOutlined /> Timestamp
          </span>
          <span className="audit-detail-value">
            {record.timestamp
              ? moment(record.timestamp).format('MMM DD, YYYY  •  hh:mm:ss A')
              : '—'}
          </span>
        </div>
        {record.errorMessage && (
          <div className="audit-detail-item full-width">
            <span className="audit-detail-label" style={{ color: '#ff4d4f' }}>Error Message</span>
            <span className="audit-detail-value" style={{ color: '#ff4d4f', fontWeight: 500 }}>
              {record.errorMessage}
            </span>
          </div>
        )}
      </div>

      {/* ── Old / New Values Section ──────────── */}
      {hasDiff && (
        <>
          <hr className="audit-detail-divider" style={{ margin: '20px 0' }} />
          <div className="audit-detail-section-title">
            <ThunderboltOutlined /> Value Changes
          </div>
          <div className="audit-diff-container">
            <div className="audit-diff-box diff-before">
              <div className="audit-diff-title diff-before">Old Values</div>
              <pre className="audit-diff-content">
                {formatJson(record.oldValues) || '—'}
              </pre>
            </div>
            <div className="audit-diff-box diff-after">
              <div className="audit-diff-title diff-after">New Values</div>
              <pre className="audit-diff-content">
                {formatJson(record.newValues) || '—'}
              </pre>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default AuditDetailModal;
